import { useEffect, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import type { Keg, KegFilters, KegStatusOption, Pagination } from '@/types'
import { pluralize, type PluralTemplate } from '@/lib/pluralize'
import { formatRelativeTime } from '@/lib/formatRelativeTime'
import { KegStatusBadge } from '@/components/KegStatusBadge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { KegIcon } from '@/components/nav/icons'

type Labels = {
  title: string
  create_button: string
  download_selected: string
  select_all: string
  select_one_prefix: string
  selected_count: PluralTemplate
  search_placeholder: string
  status_all: string
  location_all: string
  beer_all: string
  at_risk: string
  empty_state: string
  empty_title: string
  empty_body: string
  clear_filters: string
  columns: {
    identifier: string
    status: string
    current_beer: string
    current_location: string
    last_activity: string
  }
  pagination: {
    previous: string
    next: string
    page: string
  }
}

type Props = {
  kegs: Keg[]
  pagination: Pagination
  filters: KegFilters
  status_options: KegStatusOption[]
  location_options: string[]
  beer_options: string[]
  labels: Labels
}

type QueryOverrides = Partial<KegFilters & { page: number }>

const CONTROL =
  'rounded-xl border-[1.5px] border-line bg-card px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:border-ink focus:ring-0'
const SELECT_CONTROL =
  'select-kraft rounded-xl border-[1.5px] border-line bg-card py-2.5 pl-3.5 pr-9 text-sm text-ink focus:border-ink focus:ring-0'

export default function KegsIndex({ kegs, pagination, filters, status_options, location_options, beer_options, labels }: Props) {
  const [selectedIdentifiers, setSelectedIdentifiers] = useState<string[]>([])
  const [query, setQuery] = useState(filters.q)

  // The single source of URL truth: every navigation (search, status, sort,
  // pagination) merges over the current state here. Changing a filter or the
  // sort resets to page 1; only an explicit `page` override keeps paging.
  const go = (overrides: QueryOverrides) => {
    const merged = {
      q: 'q' in overrides ? overrides.q : filters.q,
      status: 'status' in overrides ? overrides.status : filters.status,
      location: 'location' in overrides ? overrides.location : filters.location,
      beer: 'beer' in overrides ? overrides.beer : filters.beer,
      at_risk: 'at_risk' in overrides ? overrides.at_risk : filters.at_risk,
      sort: 'sort' in overrides ? overrides.sort : filters.sort,
      page: 'page' in overrides ? overrides.page : 1,
    }

    router.get(
      '/kegs',
      {
        ...(merged.q ? { q: merged.q } : {}),
        ...(merged.status ? { status: merged.status } : {}),
        ...(merged.location ? { location: merged.location } : {}),
        ...(merged.beer ? { beer: merged.beer } : {}),
        ...(merged.at_risk ? { at_risk: true } : {}),
        ...(merged.sort ? { sort: merged.sort } : {}),
        ...(merged.page && merged.page > 1 ? { page: merged.page } : {}),
      },
      { preserveState: true, preserveScroll: true, replace: true },
    )
  }

  // A selection made under one filter stops being meaningful once the filter
  // changes the working set — but stays valid across a simple page turn.
  useEffect(
    () => setSelectedIdentifiers([]),
    [filters.q, filters.status, filters.location, filters.beer, filters.at_risk, filters.sort],
  )

  const toggleKeg = (id: string) => {
    setSelectedIdentifiers((current) =>
      current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [ ...current, id ]
    )
  }

  const toggleAll = () => {
    setSelectedIdentifiers((current) => (current.length === kegs.length ? [] : kegs.map((keg) => keg.identifier)))
  }

  const hasFilters =
    filters.q !== '' ||
    filters.status !== null ||
    filters.location !== null ||
    filters.beer !== null ||
    filters.at_risk
  const qrCodesHref = `/kegs/qr_codes?${selectedIdentifiers.map((id) => `identifiers[]=${id}`).join('&')}`

  return (
    <>
      <Head title={labels.title} />
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4">
          <h1 className="font-display text-3xl text-ink">{labels.title}</h1>
          <Button href="/kegs/batch/new" className="shrink-0">
            ＋ {labels.create_button}
          </Button>
        </div>

        {selectedIdentifiers.length > 0 ? (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-flame-tint px-4 py-2.5">
            <span className="text-sm font-bold text-ink">{pluralize(selectedIdentifiers.length, labels.selected_count)}</span>
            <div className="flex items-center gap-4">
              <a
                href={qrCodesHref}
                className="rounded-lg border-[1.5px] border-ink bg-card px-3 py-1.5 text-sm font-bold text-ink hover:-translate-y-0.5 motion-reduce:transform-none"
              >
                {labels.download_selected}
              </a>
              <button onClick={() => setSelectedIdentifiers([])} aria-label="×" className="text-lg leading-none text-ink-soft hover:text-ink">
                ×
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              go({ q: query })
            }}
            className="mt-6 flex flex-wrap items-center gap-3"
          >
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={labels.search_placeholder}
              className={`w-full sm:w-72 ${CONTROL}`}
            />
            <select
              value={filters.status ?? ''}
              onChange={(e) => go({ status: (e.target.value || null) as KegFilters['status'] })}
              className={SELECT_CONTROL}
            >
              <option value="">{labels.status_all}</option>
              {status_options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} ({option.count})
                </option>
              ))}
            </select>
            {location_options.length > 0 && (
              <select
                value={filters.location ?? ''}
                onChange={(e) => go({ location: e.target.value || null })}
                className={SELECT_CONTROL}
              >
                <option value="">{labels.location_all}</option>
                {location_options.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            )}
            {beer_options.length > 0 && (
              <select
                value={filters.beer ?? ''}
                onChange={(e) => go({ beer: e.target.value || null })}
                className={SELECT_CONTROL}
              >
                <option value="">{labels.beer_all}</option>
                {beer_options.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={() => go({ at_risk: !filters.at_risk })}
              aria-pressed={filters.at_risk}
              className={`rounded-xl border-[1.5px] px-3.5 py-2.5 text-sm font-bold transition-colors ${
                filters.at_risk
                  ? 'border-flame bg-flame-tint text-flame-deep'
                  : 'border-line bg-card text-ink-soft hover:text-ink'
              }`}
            >
              {labels.at_risk}
            </button>
            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setQuery('')
                  go({ q: '', status: null, location: null, beer: null, at_risk: false })
                }}
                className="text-sm font-semibold text-ink-soft hover:text-ink"
              >
                {labels.clear_filters}
              </button>
            )}
          </form>
        )}

        {kegs.length === 0 ? (
          hasFilters ? (
            <div className="mt-6">
              <EmptyState>
                <p>{labels.empty_state}</p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery('')
                    go({ q: '', status: null, location: null, beer: null, at_risk: false })
                  }}
                  className="mt-2 text-sm font-bold text-flame hover:text-flame-deep"
                >
                  {labels.clear_filters}
                </button>
              </EmptyState>
            </div>
          ) : (
            // Truly empty (no kegs yet): the value-selling first-run state.
            <div className="mt-8 rounded-2xl border border-line bg-card px-6 py-14 text-center shadow-soft">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-leaf-tint text-leaf-deep">
                <KegIcon className="h-7 w-7" />
              </span>
              <h2 className="mt-5 font-display text-2xl text-ink">{labels.empty_title}</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-ink-soft">{labels.empty_body}</p>
              <Button href="/kegs/batch/new" className="mt-6">
                ＋ {labels.create_button}
              </Button>
            </div>
          )
        ) : (
          <>
            <Card className="mt-6 hidden overflow-hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-paper-2 text-xs uppercase tracking-wider text-ink-faint">
                    <th className="w-10 py-3 pl-4">
                      <input
                        type="checkbox"
                        checked={kegs.length > 0 && selectedIdentifiers.length === kegs.length}
                        onChange={toggleAll}
                        aria-label={labels.select_all}
                      />
                    </th>
                    <th className="py-3 text-left font-bold">
                      <button type="button" onClick={() => go({ sort: 'identifier' })} className="hover:text-ink">
                        {labels.columns.identifier}
                      </button>
                    </th>
                    <th className="py-3 text-left font-bold">
                      <button type="button" onClick={() => go({ sort: 'status' })} className="hover:text-ink">
                        {labels.columns.status}
                      </button>
                    </th>
                    <th className="py-3 text-left font-bold">{labels.columns.current_beer}</th>
                    <th className="py-3 text-left font-bold">{labels.columns.current_location}</th>
                    <th className="py-3 text-left font-bold">
                      <button type="button" onClick={() => go({ sort: 'recent' })} className="hover:text-ink">
                        {labels.columns.last_activity}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {kegs.map((keg) => (
                    <tr key={keg.identifier} className="border-t border-line hover:bg-paper-2">
                      <td className="py-3 pl-4">
                        <input
                          type="checkbox"
                          checked={selectedIdentifiers.includes(keg.identifier)}
                          onChange={() => toggleKeg(keg.identifier)}
                          aria-label={`${labels.select_one_prefix} ${keg.identifier}`}
                        />
                      </td>
                      <td className="py-3">
                        <Link href={`/kegs/${keg.identifier}`} className="font-extrabold text-ink hover:text-flame">
                          {keg.identifier}
                        </Link>
                      </td>
                      <td className="py-3">
                        <KegStatusBadge keg={keg} className="text-ink-soft" />
                      </td>
                      <td className="py-3 text-ink-soft">{keg.current_beer ?? '—'}</td>
                      <td className="py-3 text-ink-soft">{keg.current_location ?? '—'}</td>
                      <td className="py-3 text-ink-faint">{formatRelativeTime(keg.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            <div className="mt-6 md:hidden">
              <button onClick={toggleAll} className="text-sm font-semibold text-ink-soft hover:text-ink">
                {labels.select_all}
              </button>
              <Card className="mt-2 overflow-hidden">
                <ul>
                  {kegs.map((keg) => (
                    <li key={keg.identifier} className="flex items-start gap-3 border-t border-line px-4 py-3 first:border-t-0">
                      <input
                        type="checkbox"
                        checked={selectedIdentifiers.includes(keg.identifier)}
                        onChange={() => toggleKeg(keg.identifier)}
                        aria-label={`${labels.select_one_prefix} ${keg.identifier}`}
                        className="mt-1"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <Link href={`/kegs/${keg.identifier}`} className="font-extrabold text-ink hover:text-flame">
                            {keg.identifier}
                          </Link>
                          <KegStatusBadge keg={keg} className="shrink-0 text-sm text-ink-soft" />
                        </div>
                        <p className="mt-0.5 truncate text-sm text-ink-soft">
                          {keg.current_beer ?? '—'} · {keg.current_location ?? '—'}
                        </p>
                        <p className="mt-0.5 text-xs text-ink-faint">{formatRelativeTime(keg.updated_at)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </>
        )}

        {pagination.pages > 1 && (
          <div className="mt-5 flex items-center gap-4 text-sm text-ink-soft">
            {pagination.page > 1 && (
              <button type="button" onClick={() => go({ page: pagination.page - 1 })} className="font-semibold hover:text-ink">
                {labels.pagination.previous}
              </button>
            )}
            <span>
              {labels.pagination.page} {pagination.page} / {pagination.pages}
            </span>
            {pagination.page < pagination.pages && (
              <button type="button" onClick={() => go({ page: pagination.page + 1 })} className="font-semibold hover:text-ink">
                {labels.pagination.next}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  )
}
